import {
  css, ThemeProvider,
} from '@emotion/react';
import {
  forwardRef, useCallback, useMemo, useState,
} from 'react';
import {
  AccordionComponent, AccordionHeaderSize,
} from '~/_shared/baseComponents/accordion';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import { getThemeForType } from '~/app/appRoot/themeWrapper/themeWrapper.helpers';
import maneuversSprite from '../../../../assets/images/maneuvers.svg';
import { createColor } from '../../_shared/components/colorPicker/colorPicker.helpers';
import { type ColorResult } from '../../_shared/components/colorPicker/colorPicker.types';
import {
  type Maneuver, type UnitSystem,
} from '../../_shared/types/googleMaps/googleMaps.types';
import { useTranslation } from '../../_shared/utils/hooks';
import { useModal } from '../../modal/useModal.hook';
import { type RouteUiData } from '../../store/frontendState/mapTools/directions/directions.state';
import {
  type MapSettingsRoute, type RouteId,
} from '../../store/mapSettings/directions/mapSettingsDirections.state';
import { getDefaultOrCustomRouteName } from '../panel/directionsPanel.helpers';
import {
  DirectionsWaypointPosition, getDirectionsTileColors,
} from '../panel/input/directionsPanelInput.helpers';
import {
  getDistanceLabel,
  getDurationLabel,
  getManeuverBackgroundPosition,
  getRouteEstimation,
} from './directionsListing.helpers';
import { LegHeaderComponent } from './legHeader/legHeader.component';
import { RouteHeaderComponent } from './routeHeader.component';

const accordionItemStyle = css({
  position: 'relative', // make the accordion item container of loader
});

const accordionItemButtonStyle = css({
  width: '100%',
  boxSizing: 'border-box',
});

const headerWrapperStyle = css({
  overflow: 'hidden',
});

const accordionPanelStyle = css({
  padding: 0,
});

const routeStepStyle = (theme: Theme) => css({
  position: 'relative',
  padding: '15px 15px 15px 45px',
  background: theme.directionsPanelColors.routeStepBackground,
  borderBottom: `1px solid ${theme.borderColors.primary}`,
  display: 'flex',
  fontSize: '14px',
  fontWeight: 400,
});

const stepEstimationStyle = css({
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  marginLeft: 'auto',
});

const stepInstructionsStyle = css({
  marginRight: 8,
});

const copyrightsStyle = (theme: Theme) => css({
  color: theme.textColors.primary,
  borderTop: `1px solid ${theme.borderColors.primary}`,
  padding: '8px 0 8px 30px',
  fontSize: 12,
});

const maneuverStyle = ({ maneuver }: Readonly<{ maneuver: Maneuver }>) => (theme: Theme) => css({
  visibility: !maneuver ? 'hidden' : 'visible',
  backgroundSize: '19px 630px',
  position: 'absolute',
  left: 15,
  top: '50%',
  width: 16,
  height: 16,
  marginTop: -8,
  filter: theme.name === 'dark' ? 'invert()' : undefined,
  backgroundImage: `url(${maneuversSprite})`,
  backgroundPosition: getManeuverBackgroundPosition(maneuver),
});

const rootPrintStyle = css({
  display: 'none',
  '@media print': {
    display: 'block',
  },
});

type DirectionsListingProps = Readonly<{
  expandedRouteIds: ReadonlySet<RouteId>;
  hiddenRouteIds: ReadonlySet<RouteId>;
  routes: ReadonlyArray<MapSettingsRoute>;
  routesUiData: ReadonlyMap<RouteId, RouteUiData>;
  unitSystem: UnitSystem;

  collapseLeg: (routeId: RouteId, legIndex: number) => void;
  expandLeg: (routeId: RouteId, legIndex: number) => void;
  onLoadDirections: (routeId: RouteId) => void;
  onRouteEdit: (routeId: RouteId, name: string, color: ColorResult) => void;
  onRouteExport?: (routeId: RouteId) => void;
  onRouteRemove?: (route: MapSettingsRoute) => void;
  onShowRoute: (routeId: RouteId) => void;
  onHideRoute: (routeId: RouteId) => void;
  collapseRoute: (routeId: RouteId) => void;
  expandRoute: (routeId: RouteId) => void;
  onZoomToRoute: (routeId: RouteId) => void;
  isPrintVersion?: boolean;
}>;

export const DirectionsListingComponent = forwardRef<HTMLDivElement, DirectionsListingProps>(({
  expandedRouteIds,
  hiddenRouteIds,
  isPrintVersion,
  collapseLeg,
  expandLeg,
  onLoadDirections,
  onHideRoute,
  onShowRoute,
  onRouteEdit,
  onRouteExport,
  onRouteRemove,
  collapseRoute,
  expandRoute,
  onZoomToRoute,
  routes,
  routesUiData,
  unitSystem,
}, forwardedRef) => {
  const [routeVisibleSettingsIndex, setRouteVisibleSettingsIndex] = useState<null | number>(null);
  const [t] = useTranslation();
  const theme = useTheme();
  const { openModal, closeModal } = useModal('DirectionsEditRoute');

  const hideSettingsMenu = useCallback(() => setRouteVisibleSettingsIndex(null), []);
  const showSettingsMenu = useCallback((routeItemIndex: number) => setRouteVisibleSettingsIndex(routeItemIndex), []);

  const onEditRouteClick = useCallback((routeId: string) => {
    const onSave = (name: string, color: ColorResult) => {
      onRouteEdit(routeId, name, color);
      closeModal();
    };

    openModal({
      routeName: getDefaultOrCustomRouteName(routes.find(r => r.id === routeId)?.name ?? '', t),
      routeColor: createColor(routes.find(r => r.id === routeId)?.color ?? '#000000'),
      onSave,
    });
  }, [closeModal, onRouteEdit, openModal, routes, t]);

  const onDirectionsRemove = useMemo(() =>
    onRouteRemove ? (route: MapSettingsRoute) => () => onRouteRemove(route) : undefined,
  [onRouteRemove]);

  const accordionData = useMemo(() => {
    return routes.map((routeItem, routeItemIndex) => {
      const uiData = routesUiData.get(routeItem.id);

      const visibilityProps = {
        isVisible: !hiddenRouteIds.has(routeItem.id),
        onHideRoute: () => onHideRoute(routeItem.id),
        onShowRoute: () => onShowRoute(routeItem.id),
      };

      if (!uiData) {
        return {
          header: (
            <RouteHeaderComponent
              isMenuVisible={false}
              routeItem={routeItem}
              totalDistance={0}
              totalDuration={0}
              unitSystem={unitSystem}
              onRouteExport={onRouteExport ? () => {
                onRouteExport?.(routeItem.id);
              } : undefined}
              isLoading
              {...visibilityProps}
            />
          ),
          child: null,
          isLocked: true,
          isExpanded: false,
        };
      }

      const routeEstimation = getRouteEstimation(uiData.legs);
      const totalDistance = routeEstimation.distance;
      const totalDuration = routeEstimation.duration;

      //it's important that boolean type variable is passed here, because 'undefined' results in conflicting behavior
      const isExpanded: boolean = expandedRouteIds.has(routeItem.id) || !!isPrintVersion;

      return ({
        isExpanded,
        onHeadingClick: () => isExpanded ? collapseRoute(routeItem.id) : expandRoute(routeItem.id),
        header: (
          <RouteHeaderComponent
            isMenuVisible={routeVisibleSettingsIndex === routeItemIndex}
            onEditRoute={() => onEditRouteClick(routeItem.id)}
            onHideMenu={hideSettingsMenu}
            onLoadDirections={() => onLoadDirections(routeItem.id)}
            onRemoveRoute={onDirectionsRemove?.(routeItem)}
            onShowMenu={() => showSettingsMenu(routeItemIndex)}
            onZoomToRoute={() => onZoomToRoute(routeItem.id)}
            routeItem={routeItem}
            totalDistance={totalDistance}
            totalDuration={totalDuration}
            unitSystem={unitSystem}
            isLoading={false}
            onRouteExport={onRouteExport ? () => {
              onRouteExport?.(routeItem.id);
            } : undefined}
            {...visibilityProps}
          />
        ),
        child: (
          <div>
            {isExpanded && uiData.legs.map((leg, legIndex) => {
              const isExpanded = uiData.expandedLegsIndexes.has(legIndex) || isPrintVersion;
              const tileColors = getDirectionsTileColors(legIndex === 0 ? DirectionsWaypointPosition.Start : DirectionsWaypointPosition.Middle, theme, routeItem.color);
              const lastTileColors = getDirectionsTileColors(DirectionsWaypointPosition.End, theme, routeItem.color);

              return (
                <div key={legIndex}>
                  <LegHeaderComponent
                    number={legIndex + 1}
                    address={leg.start_address}
                    colorTileBackground={tileColors.background}
                    colorTileColor={tileColors.textColor}
                    isExpandable
                    estimation={leg.distance && leg.duration ?
                      `${getDistanceLabel(leg.distance.value, unitSystem)} / ${getDurationLabel(leg.duration.value, t)}`
                      : undefined
                    }
                    isExpanded={isExpanded}
                    onClick={() => isExpanded ? collapseLeg(routeItem.id, legIndex) : expandLeg(routeItem.id, legIndex)}
                  />

                  {isExpanded && leg.steps.map((step, stepIndex) => (
                    <div
                      key={stepIndex}
                      css={routeStepStyle}
                    >
                      <span css={maneuverStyle({ maneuver: step.maneuver })} />
                      <div
                        css={stepInstructionsStyle}
                        dangerouslySetInnerHTML={{ __html: step.instructions }}
                      />
                      {step.distance &&
                        <div css={stepEstimationStyle}>{getDistanceLabel(step.distance.value, unitSystem)}</div>
                      }
                    </div>
                  ))}

                  {legIndex === uiData.legs.length - 1 && (
                    <LegHeaderComponent
                      address={leg.end_address}
                      colorTileBackground={lastTileColors.background}
                      colorTileColor={lastTileColors.textColor}
                      number={uiData.legs.length + 1}
                      isExpandable={false}
                    />
                  )}
                </div>
              );
            })}

            <div css={copyrightsStyle}>
              {uiData.copyrights}
            </div>
          </div>
        ),
      });
    });
  }, [
    expandedRouteIds, isPrintVersion, onDirectionsRemove, onEditRouteClick, collapseLeg, expandLeg,
    onLoadDirections, onRouteExport, collapseRoute, expandRoute, onZoomToRoute, hideSettingsMenu,
    showSettingsMenu, routeVisibleSettingsIndex, routes, routesUiData, t, theme, unitSystem, hiddenRouteIds,
    onHideRoute, onShowRoute,
  ]);

  return (
    <ThemeProvider theme={isPrintVersion ? getThemeForType('light') : theme}>
      <div
        ref={forwardedRef}
        css={isPrintVersion ? rootPrintStyle : undefined}
      >
        <AccordionComponent
          headerSize={AccordionHeaderSize.Flexible}
          itemStyle={accordionItemStyle}
          itemButtonStyle={accordionItemButtonStyle}
          headerWrapperStyle={headerWrapperStyle}
          panelStyle={accordionPanelStyle}
          data={accordionData}
        />
      </div>
    </ThemeProvider>
  );
});
